import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { InlineMessage } from "@/components/settings/settings-ui";
import { ReadAloudPassage } from "@/components/spoken-memory/EnrollmentPassage";
import {
  enrollmentRedoCopy,
  spokenMemoryApi,
  type SpokenMemoryApi,
  type VoiceProfile,
  useBrowserEnrollmentMutation,
  useRetrySpeakerSetupMutation,
} from "@/data/spoken-memory";

// Browser voice enrollment recording flow (plan 027 phase 3, extracted for
// plan 029): record with MediaRecorder, upload for server-side QA, then
// either wait for the hosted voiceprint mint (Settings) or complete on
// sample acceptance (onboarding wizard — the mint finishes in background).
export const RECORD_SECONDS = 30;
const READY_POLL_INTERVAL_MS = 2_000;
const READY_POLL_ATTEMPTS = 30;
// Chrome/Firefox record webm/opus; Safari records mp4/aac. The backend
// transcodes either to canonical WAV before QA.
const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
];

type EnrollmentFailureKind =
  | "no_microphone"
  | "permission_denied"
  | "unsupported"
  | "other";

// The plan's explicit post-recording states: the user must never see an
// ambiguous spinner. uploading = request body in flight; checking = server
// QA running; finishing = hosted speaker setup minting.
type EnrollmentPhase =
  | { kind: "idle" }
  | { kind: "preparing" }
  | { kind: "recording"; remainingSec: number }
  | { kind: "uploading" }
  | { kind: "checking" }
  | { kind: "finishing" }
  | { kind: "finishing_background" }
  | { kind: "ready" }
  | { kind: "needs_redo"; reason: string | null }
  | { kind: "setup_failed" }
  | { kind: "failed"; error: string; failure: EnrollmentFailureKind };

export function useVoiceEnrollmentRecording({
  api = spokenMemoryApi,
  workspaceSlug,
  waitForReady,
  onCompleted,
}: {
  api?: SpokenMemoryApi;
  workspaceSlug?: string | null;
  /**
   * True (Settings): after an accepted sample, poll until the hosted
   * voiceprint is ready. False (onboarding): an accepted sample completes the
   * flow immediately — the mint finishes in the background.
   */
  waitForReady: boolean;
  onCompleted: () => void | Promise<void>;
}) {
  const enroll = useBrowserEnrollmentMutation(workspaceSlug);
  const retrySetup = useRetrySpeakerSetupMutation(workspaceSlug);
  const [phase, setPhase] = useState<EnrollmentPhase>({ kind: "idle" });
  const [consented, setConsented] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const countdownRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);
  const pollTokenRef = useRef(0);

  const teardownRecording = useCallback(() => {
    if (countdownRef.current != null) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.stop();
      } catch {
        // Already stopped.
      }
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const abortRun = useCallback(() => {
    cancelledRef.current = true;
    pollTokenRef.current += 1;
    teardownRecording();
  }, [teardownRecording]);

  const reset = useCallback(() => {
    cancelledRef.current = false;
    setPhase({ kind: "idle" });
    setMessage(null);
    setConsented(false);
  }, []);

  // Stop tracks and timers on unmount.
  useEffect(() => abortRun, [abortRun]);

  const pollUntilReady = useCallback(async () => {
    const token = ++pollTokenRef.current;
    for (let attempt = 0; attempt < READY_POLL_ATTEMPTS; attempt += 1) {
      await sleep(READY_POLL_INTERVAL_MS);
      if (token !== pollTokenRef.current) return;
      let profile: VoiceProfile | null = null;
      try {
        profile = (await api.getMyVoiceProfile()).voice_profile;
      } catch {
        continue;
      }
      if (token !== pollTokenRef.current) return;
      if (profile?.attribution_status === "ready") {
        setPhase({ kind: "ready" });
        void onCompleted();
        return;
      }
      if (profile?.hosted_voiceprint_status === "failed") {
        setPhase({ kind: "setup_failed" });
        return;
      }
    }
    if (token === pollTokenRef.current) {
      setPhase({ kind: "finishing_background" });
    }
  }, [api, onCompleted]);

  const retrySpeakerSetup = useCallback(async () => {
    setPhase({ kind: "finishing" });
    try {
      await retrySetup.mutateAsync();
      await pollUntilReady();
    } catch (error) {
      if (cancelledRef.current) return;
      setPhase({ kind: "failed", error: String(error), failure: "other" });
    }
  }, [pollUntilReady, retrySetup]);

  const uploadRecording = useCallback(
    async (audio: Blob) => {
      setPhase({ kind: "uploading" });
      try {
        const result = await enroll.mutateAsync({
          audio,
          mimeType: audio.type,
          onUploadComplete: () => {
            if (cancelledRef.current) return;
            setPhase((current) =>
              current.kind === "uploading" ? { kind: "checking" } : current,
            );
          },
        });
        if (cancelledRef.current) return;
        if (result.sample_status === "needs_redo") {
          setPhase({
            kind: "needs_redo",
            reason: result.sample_status_reason,
          });
          void onCompleted();
          return;
        }
        if (!waitForReady) {
          setPhase({ kind: "ready" });
          void onCompleted();
          return;
        }
        setPhase({ kind: "finishing" });
        void onCompleted();
        await pollUntilReady();
      } catch (error) {
        if (cancelledRef.current) return;
        setPhase({ kind: "failed", error: String(error), failure: "other" });
      }
    },
    [enroll, onCompleted, pollUntilReady, waitForReady],
  );

  const startRecording = useCallback(async () => {
    setMessage(null);
    if (!consented) {
      setMessage("Consent is required.");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setPhase({
        kind: "failed",
        error: "This browser does not support microphone recording.",
        failure: "unsupported",
      });
      return;
    }
    cancelledRef.current = false;
    setPhase({ kind: "preparing" });
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
    } catch (error) {
      setPhase(microphoneFailure(error));
      return;
    }
    if (cancelledRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    streamRef.current = stream;
    const mimeType = PREFERRED_MIME_TYPES.find((type) =>
      MediaRecorder.isTypeSupported(type),
    );
    const recorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType } : undefined,
    );
    recorderRef.current = recorder;
    const chunks: Blob[] = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.onstop = () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      if (cancelledRef.current) return;
      const blob = new Blob(chunks, {
        type: recorder.mimeType || mimeType || "audio/webm",
      });
      void uploadRecording(blob);
    };
    recorder.start();
    setPhase({ kind: "recording", remainingSec: RECORD_SECONDS });

    let remaining = RECORD_SECONDS;
    countdownRef.current = window.setInterval(() => {
      remaining -= 1;
      if (remaining > 0) {
        setPhase({ kind: "recording", remainingSec: remaining });
        return;
      }
      if (countdownRef.current != null) {
        window.clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      recorderRef.current = null;
      if (recorder.state !== "inactive") recorder.stop();
    }, 1_000);
  }, [consented, uploadRecording]);

  const busy =
    phase.kind === "preparing" ||
    phase.kind === "recording" ||
    phase.kind === "uploading" ||
    phase.kind === "checking" ||
    phase.kind === "finishing";
  // Before the upload starts, cancelling only discards local recording state.
  // Once the sample is in flight (uploading/checking) there is no true
  // request cancellation + server cleanup.
  const canCancel =
    phase.kind === "idle" ||
    phase.kind === "preparing" ||
    phase.kind === "recording";

  return {
    phase,
    consented,
    setConsented,
    message,
    busy,
    canCancel,
    startRecording,
    retrySpeakerSetup,
    abortRun,
    reset,
  };
}

function microphoneFailure(error: unknown): EnrollmentPhase {
  const name = error instanceof DOMException ? error.name : "";
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return {
      kind: "failed",
      error:
        "No microphone was found on this device. Open Parable on a laptop or phone with a microphone to finish setup.",
      failure: "no_microphone",
    };
  }
  if (name === "NotAllowedError" || name === "SecurityError") {
    return {
      kind: "failed",
      error:
        "Microphone access was blocked. Allow microphone access for this site in your browser's site settings, then try again.",
      failure: "permission_denied",
    };
  }
  return {
    kind: "failed",
    error: "Microphone access failed. Check your microphone and try again.",
    failure: "other",
  };
}

/**
 * Shared per-phase body content (passage, progress lines, outcome messages).
 * Callers render their own idle content (copy + consent) and footer actions.
 */
export function VoiceEnrollmentPhaseView({
  phase,
}: {
  phase: EnrollmentPhase;
}) {
  switch (phase.kind) {
    case "preparing":
      return <EnrollmentStatusLine label="Preparing microphone…" />;
    case "recording":
      return (
        <ReadAloudPassage
          state="recording"
          remainingSec={phase.remainingSec}
          totalSec={RECORD_SECONDS}
        />
      );
    case "uploading":
      return <EnrollmentStatusLine label="Uploading your recording…" />;
    case "checking":
      return <EnrollmentStatusLine label="Checking your sample…" />;
    case "finishing":
      return (
        <EnrollmentStatusLine label="Sample accepted — finishing speaker setup…" />
      );
    case "finishing_background":
      return (
        <InlineMessage variant="info">
          Your sample was accepted. Speaker setup is taking longer than expected
          and will finish in the background — check back here in a minute.
        </InlineMessage>
      );
    case "ready":
      return (
        <InlineMessage variant="success">
          You&apos;re all set. Transcripts will attribute your voice from now
          on.
        </InlineMessage>
      );
    case "needs_redo":
      return (
        <InlineMessage variant="error">
          {enrollmentRedoCopy(phase.reason)}
        </InlineMessage>
      );
    case "setup_failed":
      return (
        <InlineMessage variant="error">
          Your sample is good, but speaker setup failed. Try setup again — no
          need to re-record.
        </InlineMessage>
      );
    case "failed":
      return <InlineMessage variant="error">{phase.error}</InlineMessage>;
    default:
      return null;
  }
}

function EnrollmentStatusLine({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-2 text-[13px] text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      {label}
    </div>
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
